import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2, FileText, Truck, Percent, PackageCheck, Users, ArrowRight, Calculator,
} from "lucide-react";
import api from "@/lib/api";
import { formatINR } from "@/lib/format";
import { ProductCard } from "@/components/marketplace/ProductCard";
import { VendorCard } from "@/components/marketplace/VendorCard";
import { SectionHeader } from "@/components/marketplace/SectionHeader";
import { useStore } from "@/context/StoreContext";

const FEATURES = [
  [Percent, "MOQ & Tier Pricing", "Bulk discounts that improve as your order size grows."],
  [FileText, "GST Tax Invoice", "Compliant invoicing for your business purchases."],
  [PackageCheck, "Verified Suppliers", "Source from KYC-verified manufacturers & wholesalers."],
  [Truck, "Pan India Logistics", "Bulk shipping across India with tracking."],
];

export default function B2B() {
  const { setB2bMode } = useStore();
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    setB2bMode(true);
    api.get("/products?b2b=true&limit=8").then((r) => setProducts(r.data.items));
    api.get("/vendors").then((r) => setVendors(r.data.filter((v) => v.verified)));
  }, []);

  return (
    <div>
      <section className="bg-[#1F1610] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <span className="eyebrow text-primary">B2B Wholesale Marketplace</span>
          <h1 className="mt-3 text-4xl sm:text-5xl font-serif font-bold max-w-2xl">Buy Wholesale. Grow Your Business.</h1>
          <p className="mt-4 text-stone-300 max-w-2xl">
            Connect with verified vendors, manufacturers and suppliers for bulk spiritual and traditional products with MOQ pricing, GST invoicing and pan-India logistics.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/b2b/products" data-testid="b2b-browse-btn" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold px-6 py-3">
              <Building2 size={18} /> Browse Wholesale Products
            </Link>
            <Link to="/b2b/request-quote" data-testid="b2b-rfq-btn" className="inline-flex items-center gap-2 rounded-full border border-white/25 font-semibold px-6 py-3 hover:bg-white/10">
              <FileText size={18} /> Request Bulk Quote
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {FEATURES.map(([Icon, t, d]) => (
            <div key={t} className="rounded-xl border border-border bg-card p-5">
              <div className="h-11 w-11 rounded-lg bg-accent text-secondary grid place-items-center mb-3"><Icon size={20} /></div>
              <h3 className="font-semibold text-stone-900">{t}</h3>
              <p className="mt-1 text-sm text-stone-500">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <BulkCalculator />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <SectionHeader eyebrow="Wholesale Catalog" title="Popular Wholesale Products" action={<Link to="/b2b/products" className="text-sm font-semibold text-secondary inline-flex items-center gap-1">View all <ArrowRight size={15} /></Link>} />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {products.map((p) => <ProductCard key={p.slug} product={p} />)}
        </div>
      </section>

      <section className="bg-accent/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <SectionHeader eyebrow="Directory" title="Manufacturers & Wholesalers" subtitle="Verified suppliers ready for bulk orders." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {vendors.map((v) => <VendorCard key={v.slug} vendor={v} />)}
          </div>
        </div>
      </section>
    </div>
  );
}

function BulkCalculator() {
  const [unitPrice, setUnitPrice] = useState(180);
  const [qty, setQty] = useState(50);
  const discount = qty >= 500 ? 0.12 : qty >= 100 ? 0.06 : 0;
  const total = Math.round(unitPrice * qty * (1 - discount));

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 grid md:grid-cols-2 gap-8 items-center">
        <div>
          <div className="flex items-center gap-2 text-secondary mb-2"><Calculator size={20} /><span className="eyebrow">Bulk Order Calculator</span></div>
          <h3 className="font-serif font-bold text-2xl text-stone-900">Estimate your wholesale cost</h3>
          <p className="mt-2 text-sm text-stone-500">Indicative estimate only. Final quotes are provided by vendors via RFQ.</p>
          <div className="mt-5 grid grid-cols-2 gap-4">
            <label className="text-sm">
              <span className="text-stone-500">Unit price (₹)</span>
              <input type="number" value={unitPrice} onChange={(e) => setUnitPrice(+e.target.value || 0)} data-testid="calc-unit-price" className="mt-1 w-full rounded-lg border border-border px-3 py-2 outline-none focus:ring-2 focus:ring-primary/30" />
            </label>
            <label className="text-sm">
              <span className="text-stone-500">Quantity</span>
              <input type="number" value={qty} onChange={(e) => setQty(+e.target.value || 0)} data-testid="calc-qty" className="mt-1 w-full rounded-lg border border-border px-3 py-2 outline-none focus:ring-2 focus:ring-primary/30" />
            </label>
          </div>
        </div>
        <div className="rounded-xl bg-secondary text-white p-6 text-center">
          <p className="text-sm text-white/70">Estimated total {discount > 0 && <span className="text-primary-foreground">({Math.round(discount * 100)}% bulk discount)</span>}</p>
          <p className="mt-2 text-4xl font-bold" data-testid="calc-total">{formatINR(total)}</p>
          <p className="mt-1 text-sm text-white/70">{qty} units × {formatINR(unitPrice)}</p>
          <Link to="/b2b/request-quote" className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold px-5 py-2.5">Request Firm Quote</Link>
        </div>
      </div>
    </section>
  );
}
